import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { resetRequestContext } from "../../requestContext";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

function decodeJwt(token) {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function userFromToken(token) {
  const p = token ? decodeJwt(token) : null;
  if (!p) return null;

  return {
    id: p.sub ? Number(p.sub) : null,
    user_name: p.user_name ?? null,
    tenant_db_url: p.tenant_db_url ?? null,
    perms: p.perms || [],
    branch_ids: p.branch_ids || [],
    default_branch_id: p.default_branch_id ?? null,
    is_superuser: !!p.is_superuser,
    app_version: p.app_version || "STANDARD",
    use_doc_closing: p.use_doc_closing !== false,
  };
}

export default function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("token") || "");
  const [user, setUser] = useState(() =>
    userFromToken(localStorage.getItem("token")),
  );
  const [activeBranchId, setActiveBranchIdState] = useState(() => {
    const saved = localStorage.getItem("active_branch_id");
    return saved ? Number(saved) : null;
  });

  const login = (t, timeout_minutes) => {
    localStorage.removeItem("active_tenant");
    localStorage.removeItem("active_branch_id");
    if (timeout_minutes) {
        localStorage.setItem("session_timeout_minutes", timeout_minutes);
    } else {
        localStorage.removeItem("session_timeout_minutes");
    }
    localStorage.setItem("token", t);
    resetRequestContext();
    setToken(t);
    setUser(userFromToken(t));
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("active_tenant");
    localStorage.removeItem("active_branch_id");
    localStorage.removeItem("session_timeout_minutes");
    resetRequestContext();
    setToken("");
    setUser(null);
    setActiveBranchIdState(null);
    window.location.href = "/login";
  };

  const setActiveBranchId = (id) => {
    if (id !== undefined && id !== null) {
      localStorage.setItem("active_branch_id", id);
      setActiveBranchIdState(Number(id));
    } else {
      localStorage.removeItem("active_branch_id");
      setActiveBranchIdState(null);
    }
    window.dispatchEvent(new Event("active-branch-changed"));
  };

  // Dacă axios refresh schimbă tokenul în localStorage, îl sincronizăm aici
  useEffect(() => {
    const sync = () => {
      const t = localStorage.getItem("token") || "";
      setToken(t);
      setUser(userFromToken(t));
      const b = localStorage.getItem("active_branch_id");
      setActiveBranchIdState(b ? Number(b) : null);
    };

    // 1) când noi declanșăm manual
    window.addEventListener("auth-token-changed", sync);
    window.addEventListener("active-branch-changed", sync);

    // 2) dacă se schimbă tokenul sau branch-ul din alt tab
    window.addEventListener("storage", (e) => {
      if (e.key === "token" || e.key === "active_branch_id") sync();
    });

    return () => {
      window.removeEventListener("auth-token-changed", sync);
      window.removeEventListener("active-branch-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    let intervalId;
    
    const checkIdle = () => {
      const storedTimeout = localStorage.getItem("session_timeout_minutes");
      if (!storedTimeout || storedTimeout === "0") return;
      
      const timeoutMs = Number(storedTimeout) * 60 * 1000;
      const lastActivity = Number(localStorage.getItem("last_activity") || Date.now());
      
      if (Date.now() - lastActivity > timeoutMs) {
        logout();
      }
    };

    const updateActivity = () => {
      localStorage.setItem("last_activity", Date.now());
    };

    if (token) {
      // Set initial activity
      updateActivity();
      
      // Events to track activity
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
      events.forEach(e => window.addEventListener(e, updateActivity, { passive: true }));
      
      // Check every 1 minute
      intervalId = setInterval(checkIdle, 60000);
      
      return () => {
        events.forEach(e => window.removeEventListener(e, updateActivity));
        clearInterval(intervalId);
      };
    }
  }, [token]);

  const value = useMemo(
    () => ({
      token,
      user,
      login,
      logout,
      isAuthed: !!token,
      activeBranchId,
      setActiveBranchId,
    }),
    [token, user, activeBranchId],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
